/**
 * The clock for work the timeline engine does not own.
 *
 * useTicker is bound to timeline liveness: it repaints while a run in the demo
 * store is still going and stops when none are. Two surfaces in the product
 * are clock derived without being timeline runs, and both need a repaint
 * schedule of their own:
 *
 *   S17  the live log tail, which streams for as long as somebody watches and
 *        therefore never ends the way a build does;
 *   S19  the cleaning up narration, which is a scripted checklist that has no
 *        entry in scripts.ts yet.
 *
 * The discipline is the engine's, deliberately, and it is the part that
 * matters: nothing here accumulates. The interval only schedules a repaint,
 * and every value on screen is a pure function of (now - startedAt) * speed.
 * So a backgrounded tab that gets its timers throttled comes back correct
 * instead of behind, which is exactly what breaks tick based fakes in front of
 * an audience.
 *
 * When scripts.ts grows a project-delete script, S19's caller collapses onto
 * AsyncNarration and this hook keeps only the log tail.
 */

import { useEffect, useReducer } from 'react';

export function useWallClock(running: boolean, intervalMs = 250): number {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(bump, intervalMs);
    return () => window.clearInterval(id);
  }, [running, intervalMs]);

  return Date.now();
}
